import dayjs, { Dayjs } from 'dayjs'
import { CalendarEventVisit, CrewMember, ScheduledEvent, Visit } from '../scheduleApi'
import { eventHasVisit, getDayDateRange, UNASSIGNED_CREW_ID } from '../scheduleModel'
import { emailsMatch } from '../crewModel'

export type CrewLeadSelection = number | typeof UNASSIGNED_CREW_ID

export const INSUFFICIENT_LOCATIONS_MESSAGE = 'At least 2 locations are required to optimize'
export const ALL_EVENTS_VISITED_MESSAGE = 'All scheduled events have already been visited'

export const isEventPlanned = (
  event: ScheduledEvent,
  visits: Visit[],
  calendarEventVisits: CalendarEventVisit[],
  date: Dayjs
): boolean => {
  return !eventHasVisit(event, calendarEventVisits, visits, getDayDateRange(date))
}

type ReorderableEventShape = { allDay: boolean; start: string; end: string }

export const isEventReorderable = (event: ReorderableEventShape): boolean => {
  if (event.allDay) {
    return false
  }
  const start = dayjs(event.start)
  const end = dayjs(event.end)
  if (!start.isSame(end, 'day')) {
    return false
  }
  return true
}

export const getEventsForCrewLead = (
  events: ScheduledEvent[],
  selection: CrewLeadSelection,
  crewLeadEmail: string | null
): ScheduledEvent[] => {
  return events.filter((event) => {
    if (selection === UNASSIGNED_CREW_ID) {
      return !event.crewForemanEmail || event.crewForemanEmail.trim() === ''
    }
    return emailsMatch(event.crewForemanEmail, crewLeadEmail)
  })
}

export type CrewLeadOption = {
  id: CrewLeadSelection
  name: string
  email: string | null
  avatar: string | undefined
  hasStockAvatar: boolean
  color: string | undefined
  plannedEvents: ScheduledEvent[]
}

const canBeCrewLead = (member: CrewMember): boolean => {
  return !!member.userEmail && member.userEmail.trim() !== ''
}

export const buildCrewLeadOptions = (
  crew: CrewMember[],
  events: ScheduledEvent[],
  visits: Visit[],
  calendarEventVisits: CalendarEventVisit[],
  date: Dayjs
): CrewLeadOption[] => {
  const options: CrewLeadOption[] = crew
    .filter(canBeCrewLead)
    .flatMap((member) => {
      const crewEvents = getEventsForCrewLead(events, member.id, member.userEmail ?? null)
      if (crewEvents.length === 0) {
        return []
      }
      const plannedEvents = crewEvents.filter((event) => isEventPlanned(event, visits, calendarEventVisits, date))
      return [{
        id: member.id,
        name: member.name,
        email: member.userEmail ?? null,
        avatar: member.hasStockAvatar ? undefined : member.avatar,
        hasStockAvatar: member.hasStockAvatar ?? false,
        color: member.color,
        plannedEvents,
      }]
    })

  const unassignedAllEvents = getEventsForCrewLead(events, UNASSIGNED_CREW_ID, null)

  if (unassignedAllEvents.length === 0) {
    return options
  }

  const unassignedPlannedEvents = unassignedAllEvents.filter((event) =>
    isEventPlanned(event, visits, calendarEventVisits, date)
  )

  const unassigned: CrewLeadOption = {
    id: UNASSIGNED_CREW_ID,
    name: 'Unassigned Events',
    email: null,
    avatar: undefined,
    hasStockAvatar: true,
    color: undefined,
    plannedEvents: unassignedPlannedEvents,
  }

  return [...options, unassigned]
}

export const getIneligibleReason = (option: CrewLeadOption): string | null => {
  if (option.plannedEvents.length === 0) {
    return ALL_EVENTS_VISITED_MESSAGE
  }
  if (!hasSufficientLocations(option.plannedEvents)) {
    return INSUFFICIENT_LOCATIONS_MESSAGE
  }
  return null
}

export const filterCrewLeadOptions = (options: CrewLeadOption[], search: string): CrewLeadOption[] => {
  const query = search.trim().toLowerCase()
  if (query === '') {
    return options
  }
  return options.filter((option) => option.name.toLowerCase().includes(query))
}

export const formatEventTimeRange = (event: ScheduledEvent): string => {
  if (event.allDay) {
    return 'All Day'
  }
  const start = dayjs(event.start).format('h:mm A')
  const end = dayjs(event.end).format('h:mm A')
  return `${start} - ${end}`
}

export const getWorkAreaCount = (event: ScheduledEvent): number => {
  if (!event.workAreaIds) {
    return 0
  }
  return event.workAreaIds.split(',').map((s) => s.trim()).filter((s) => s.length > 0).length
}

type WithLocation = { propertyId: number | null }

export const hasSufficientLocations = <T extends WithLocation & ReorderableEventShape>(plannedEvents: T[]): boolean => {
  const uniquePropertyIds = new Set(
    plannedEvents
      .filter(isEventReorderable)
      .map(e => e.propertyId)
      .filter((id): id is number => id !== null)
  )
  return uniquePropertyIds.size >= 2
}
